import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import moment from 'moment';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import Device from 'src/app/services/device.model';
import { DeviceService } from 'src/app/services/device.service';
import DeviceModel from 'src/app/services/deviceModel.model';
import { DeviceModelService } from 'src/app/services/device-model.service';
import { ToastrService } from 'ngx-toastr';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';

@Component({
  selector: 'app-device',
  templateUrl: './device.component.html',
  styleUrls: ['./device.component.scss']
})
export class DeviceComponent implements OnInit, OnDestroy {
  public devices: Array<Device> = [];
  public dupDevices: Array<Device> = [];
  public filteredDevices: Array<Device> = [];
  public devicesList: Array<Device> = [];
  public device: Device = new Device();
  public deviceModels: Array<DeviceModel> = [];
  public deviceModel: DeviceModel;
  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public registerForm: FormGroup;
  public submitted = false;
  public addDeviceDoubleClick: boolean = false;
  public NoDataMessage: boolean = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader: boolean = false;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  public showForm: boolean = false;
  public formFieldCount: number = 0;
  public maxDate: Date = new Date();

  private searchTimeout: any;
  private scrollTimeout: any;
  private batchLoader: BatchLoader<any>;

  constructor(
    public formBuilder: FormBuilder,
    public deviceService: DeviceService,
    public deviceModelService: DeviceModelService,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService,
    private toastr: ToastrService
  ) {
    this.device = new Device();
    this.registerForm = this.formBuilder.group({
      number: ['', [Validators.required]],
      manufacturedDate: ['', Validators.required],
      modelId: ['', [Validators.required]],
    });
  }

  async ngOnInit() {
    this.search = '';
    this.devices = [];
    this.dupDevices = [];
    this.filteredDevices = [];
    this.devicesList = [];
    this.isFiltering = false;
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    await this.init();
  }

  get f() {
    return this.registerForm.controls;
  }

  public async init() {
    try {
      this.enableDisablePageLoader = true;
      this.submitButton = false;
      this.updateButton = false;
      this.addDeviceDoubleClick = false;
      const [devs, models] = await Promise.all([
        this.deviceService.getAllDevices(),
        this.deviceModelService.getDeviceModals()
      ]);
      this.devices = devs || [];
      this.deviceModels = models || [];
      this.changeDevices();

      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error: any) {
      this.toastr.error(error);
      this.enableDisablePageLoader = false;
    }
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredDevices : this.dupDevices,
      (updatedList) => {
        this.devicesList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  public ngOnDestroy(): void {
    this.deviceService.cancelOnGoingHttpRequests();
    this.deviceModelService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  public changeDevices() {
    if (!this.devices) return;
    this.dupDevices = this.devices.map(dev => {
      const devCopy = { ...dev };
      if (devCopy.device_model_id && this.deviceModels) {
        devCopy.deviceModel = this.getDeviceModelById(String(devCopy.device_model_id));
      }
      return devCopy;
    });
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public async applySearch(): Promise<void> {
    const value = this.search && this.search.trim().replace(/[-_]|\s/g, '').toLowerCase();
    this.isFiltering = !!value;

    const sourceList = this.isFiltering
      ? this.dupDevices.filter(device =>
          (device && device.number && device.number.toLowerCase().includes(value)) ||
          (device && device.serial_number && device.serial_number.toLowerCase().includes(value)) ||
          (device && device.deviceModel && device.deviceModel.name && device.deviceModel.name.replace(/[-_]|\s/g, '').toLowerCase().includes(value))
        )
      : this.dupDevices;

    this.filteredDevices = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.devicesList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.submitted = false;
    this.device = new Device();
    this.registerForm.reset();
    this.registerForm.patchValue({
      manufacturedDate: new Date()
    });
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  public closeForm() {
    this.showForm = false;
    this.submitted = false;
    this.device = new Device();
    this.registerForm.reset();
  }

  public clear() {
    this.closeForm();
  }

  public addUpdateDevice() {
    if (this.submitButton) {
      this.addDevice();
    } else {
      this.updateDevice();
    }
  }

  public async addDevice() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    const formVal = this.registerForm.value;
    const serialNum = formVal.number ? formVal.number.trim() : '';

    let existing = this.devices.filter(d => d && (d.number === serialNum || d.serial_number === serialNum));
    if (existing && existing.length > 0) {
      this.toastr.error('Device serial number already exists');
      return;
    }

    this.addDeviceDoubleClick = true;
    this.device.number = serialNum;
    this.device.serial_number = serialNum;
    this.device.device_model_id = formVal.modelId;

    if (formVal.manufacturedDate) {
      this.device.manufactured_date = moment(formVal.manufacturedDate).format('YYYY-MM-DD HH:mm:ss');
    }

    try {
      await this.deviceService.addDevice(this.device);
      this.toastr.success('Device Created Successfully');
      this.closeForm();
      await this.init();
    } catch (error: any) {
      this.toastr.error(error);
      this.addDeviceDoubleClick = false;
    }
  }

  public async updateDevice() {
    this.submitted = true;
    if (this.registerForm.invalid) {
      return;
    }
    const formVal = this.registerForm.value;
    const serialNum = formVal.number ? formVal.number.trim() : '';

    let existing = this.devices.filter(d => d && (d.number === serialNum || d.serial_number === serialNum) && d.id !== this.device.id);
    if (existing && existing.length > 0) {
      this.toastr.error('Device serial number already exists');
      return;
    }

    this.addDeviceDoubleClick = true;
    this.device.number = serialNum;
    this.device.serial_number = serialNum;
    this.device.device_model_id = formVal.modelId;

    if (formVal.manufacturedDate) {
      this.device.manufactured_date = moment(formVal.manufacturedDate).format('YYYY-MM-DD HH:mm:ss');
    }
    this.device.updatedAt = new Date();

    try {
      const updateDevicePayload = Object.assign({}, this.device);
      delete updateDevicePayload.id;
      await this.deviceService.updateOneByID(this.device.id, updateDevicePayload);
      this.toastr.success('Device Updated successfully');
      this.closeForm();
      await this.init();
    } catch (error: any) {
      if (error === 'Validation error') {
        this.toastr.error('Device serial number already exists');
      } else {
        this.toastr.error(error);
      }
      this.addDeviceDoubleClick = false;
    }
  }

  public getDeviceById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.submitted = false;
    this.addDeviceDoubleClick = false;
    const found = this.dupDevices.find(device => device && device.id === id);
    if (found) {
      this.device = Object.assign({}, found);
      this.registerForm.patchValue({
        number: this.device.number || this.device.serial_number || '',
        modelId: this.device.device_model_id || '',
        manufacturedDate: this.device.manufactured_date ? new Date(this.device.manufactured_date) : null
      });
    }
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
    return this.device;
  }

  public getDeviceModelById(id: string) {
    if (!this.deviceModels) return undefined;
    return this.deviceModels.find(deviceModel => deviceModel && String(deviceModel.id) === String(id));
  }
}
